import iconComment from "@assets/images/comment-customer.svg";
import paperTeal from "@assets/images/paper-teal.svg";
import { Button, Grid } from "@material-ui/core";
import DatePicker from "@saleor/components/DatePicker";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextAreaField from "@saleor/components/TextAreaField";
import DialogError from "@saleor/createOrderExport/components/DialogError";
import {
  ExportOrderWithAllItemsQuery,
  OrderType,
  RealtimePartnerType,
  useGet_Realtime_PartnerMutation
} from "@saleor/graphql";
import useDebounce from "@saleor/hooks/useDebounce";
import { getRandomNumber } from "@saleor/utils/getRandowNumber";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getFormik } from "./formik";
import ModalAddress from "./ModalAddress";
import useStyles from "./styles";

interface OrderHeaderTabProps {
  dataExportOrder?: ExportOrderWithAllItemsQuery["exportOrderWithAllItems"];
  applyAll?: number;
  onApplyAll?: () => void;
  handleApplyAll: (value) => void;
  saveDraft?: number;
  submitOrderCall?: number;
  netPrice: number;
  setCollectDataSubmit?: (value) => void;
}

const usageOptions = [
  {
    label: "",
    value: ""
  },
  {
    label: "100 - Container",
    value: "100"
  },
  {
    label: "200 - Break bulk",
    value: "200"
  },
  {
    label: "E - Replacement",
    value: "E"
  },
  {
    label: "M - Sample",
    value: "M"
  },
  {
    label: "S - Series",
    value: "S"
  }
];

const OrderHeaderTab: React.FC<OrderHeaderTabProps> = props => {
  const {
    dataExportOrder,
    applyAll,
    onApplyAll,
    handleApplyAll,
    saveDraft,
    submitOrderCall,
    netPrice,
    setCollectDataSubmit
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const [typeAddress, setTypeAddress] = useState<RealtimePartnerType>(
    RealtimePartnerType.SHIP_TO
  );
  const [visibleModalAddress, setVisibleModalAddress] = useState<boolean>(
    false
  );
  const [visibleDialogError, setVisibleDialogError] = useState<boolean>(false);

  const formik = getFormik(dataExportOrder);

  const [
    getRealtimePartnerData,
    { data: realtimePartnerData }
  ] = useGet_Realtime_PartnerMutation({
    variables: {
      type: [RealtimePartnerType.SHIP_TO, RealtimePartnerType.BILL_TO],
      saleOrgCode:
        dataExportOrder?.salesOrganization?.code ||
        dataExportOrder?.pi?.salesOrganization?.code,
      soldToCode: dataExportOrder?.pi?.soldTo?.code,
      piMessageId: getRandomNumber(),
      distributionChannel: dataExportOrder?.distributionChannel?.code,
      division: dataExportOrder?.division?.code,
      orderType: OrderType.EXPORT
    }
  });
  const getRealtimePartnerDebounce = useDebounce(getRealtimePartnerData, 200);

  // const [getShipToData, { data: shipToData }] = useGet_Realtime_PartnerMutation(
  //   {
  //     variables: {
  //       type: RealtimePartnerType.SHIP_TO,
  //       saleOrgCode:
  //         dataExportOrder?.salesOrganization?.code ||
  //         dataExportOrder?.pi?.salesOrganization?.code,
  //       soldToCode: dataExportOrder?.pi?.soldTo?.code,
  //       piMessageId: `${Math.floor(Math.random() * 1000)}`,
  //       distributionChannel: dataExportOrder?.distributionChannel?.code,
  //       division: dataExportOrder?.division?.code,
  //       orderType: OrderType.EXPORT
  //     }
  //   }
  // );

  // const [getBillToData, { data: billToData }] = useGet_Realtime_PartnerMutation(
  //   {
  //     variables: {
  //       type: RealtimePartnerType.BILL_TO,
  //       saleOrgCode:
  //         dataExportOrder?.salesOrganization?.code ||
  //         dataExportOrder?.pi?.salesOrganization?.code,
  //       soldToCode: dataExportOrder?.pi?.soldTo?.code,
  //       piMessageId: `${Math.floor(Math.random() * 1000)}`,
  //       distributionChannel: dataExportOrder?.distributionChannel?.code,
  //       division: dataExportOrder?.division?.code,
  //       orderType: OrderType.EXPORT
  //     }
  //   }
  // );

  useEffect(() => {
    if (dataExportOrder?.pi) {
      getRealtimePartnerDebounce();
    }
  }, [dataExportOrder]);

  const handleApplyAddress = (value: string, type: RealtimePartnerType) => {
    switch (type) {
      case RealtimePartnerType.SHIP_TO:
        formik.setFieldValue("shipTo", value);
        break;
      case RealtimePartnerType.BILL_TO:
        formik.setFieldValue("billTo", value);
        break;
    }
  };

  const handleOpenAddress = (type: RealtimePartnerType) => {
    setTypeAddress(type);
    getRealtimePartnerData();
    setVisibleModalAddress(true);
  };

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  useEffect(() => {
    if (applyAll) {
      if (!formik?.errors?.requestDate) {
        handleApplyAll(formik.values);
      } else {
        setVisibleDialogError(true);
      }
    }
  }, [applyAll]);

  useEffect(() => {
    if (submitOrderCall) {
      if (formik.isValid) {
        setCollectDataSubmit(formik.values);
      } else {
        setCollectDataSubmit(false);
      }
    }
  }, [submitOrderCall]);

  useEffect(() => {
    if (saveDraft) {
      setCollectDataSubmit(formik.values);
    }
  }, [saveDraft]);

  const handleChangeEtd = event => {
    formik.setFieldValue("etd", event.target.value);
    formik.setFieldValue(
      "requestDate",
      moment(event.target.value)
        .subtract(10, "d")
        .format("YYYY-MM-DD")
    );
  };

  const handleChangeEta = event => {
    formik.setFieldValue("eta", event.target.value);
  };

  const mappedRealtimePartnerData = useMemo(
    () => realtimePartnerData?.getSapRealtimePartner?.response,
    [realtimePartnerData]
  );

  const shipToData = useMemo(
    () => mappedRealtimePartnerData?.filter(e => e.partnerFunction === "WE"),
    [mappedRealtimePartnerData]
  );

  const billToData = useMemo(
    () => mappedRealtimePartnerData?.filter(e => e.partnerFunction === "RE"),
    [mappedRealtimePartnerData]
  );

  const isDisplayShipTo = useMemo(() => {
    if (
      shipToData?.find(e => e.addressText === formik.values.shipTo) &&
      shipToData?.length
    ) {
      return true;
    }
    if (shipToData?.length > 1) {
      return true;
    }
    return false;
  }, [shipToData, formik.values.shipTo]);

  const isDisplayBillTo = useMemo(() => {
    if (
      billToData?.find(e => e.addressText === formik.values.shipTo) &&
      billToData?.length
    ) {
      return true;
    }
    if (billToData?.length > 1) {
      return true;
    }
    return false;
  }, [billToData, formik.values.shipTo]);

  const handleDate = e => {
    formik.handleChange(e);
  };

  return (
    <div className={classes.root}>
      <div className={classes.piNo}>
        <img src={paperTeal} alt="icon" />
        <MyTypography variant={"h7"} className={classes.itemPiNo}>
          PI No.
        </MyTypography>
        <MyTypography variant={"h6"} className={classes.itemPiNo}>
          {dataExportOrder?.pi?.code}
        </MyTypography>
      </div>
      <div className={classes.mainContent}>
        <Grid container spacing={6}>
          <Grid item md={6} sm={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Sold-To Party" })}
                </MyTypography>
                <div className={classes.soldToInfo}>
                  <MyTypography variant={"body1"} className={classes.textCode}>
                    {dataExportOrder?.pi?.soldTo?.displayText}
                  </MyTypography>
                  <MyTypography
                    variant={"body3"}
                    className={classes.textAddress}
                  >
                    {dataExportOrder?.soldToAddress}
                  </MyTypography>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "PO No." })}
                </MyTypography>
                <SingleTextField
                  name={"poNo"}
                  id="poNo"
                  value={formik.values.poNo || ""}
                  onChange={formik.handleChange}
                  error={!!formik.errors.poNo}
                  maxLengthProps={35}
                />
                {getTextError("poNo")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Ref. PI No." })}
                </MyTypography>
                <SingleTextField
                  name={"refPiNo"}
                  id="refPiNo"
                  value={formik.values.refPiNo || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={40}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "PO Date" })}
                </MyTypography>
                <DatePicker
                  name={"poDate"}
                  id="poDate"
                  value={formik.values.poDate}
                  onChange={handleDate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container alignItems="flex-end" spacing={2}>
                  <Grid item xs={7}>
                    <MyTypography
                      variant={"subtitle3"}
                      className={classes.titleField}
                    >
                      {intl.formatMessage({ defaultMessage: "Request Date" })}
                    </MyTypography>
                    <DatePicker
                      name={"requestDate"}
                      id="requestDate"
                      value={formik.values.requestDate}
                      onChange={handleDate}
                      error={!!formik.errors.requestDate}
                      noBlock
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Button
                      variant="contained"
                      className={classes.applyBtn}
                      onClick={onApplyAll}
                      name="apply-all-button"
                      id="apply-all-button"
                      data-testid="request-date-apply-all-button"
                    >
                      {intl.formatMessage({
                        defaultMessage: "Apply all",
                        description: "button label"
                      })}
                    </Button>
                  </Grid>
                </Grid>
                {getTextError("requestDate")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Net Price" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  <DecimalFormat
                    value={netPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Doc. Currency" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.docCurrency}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Terms of payment" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.paymentTerm}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Incoterms" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.pi?.incoterm}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.selectField}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Usage" })}
                </MyTypography>
                <SingleSelectField
                  name={"usage"}
                  id="usage"
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  placeholder=""
                  choices={usageOptions}
                  value={formik.values.usage || ""}
                  onChange={formik.handleChange}
                  // maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Unloading Point" })}
                </MyTypography>
                <SingleTextField
                  name={"unloadingPoint"}
                  id="unloadingPoint"
                  value={formik.values.unloadingPoint || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Place of Delivery" })}
                </MyTypography>
                <SingleTextField
                  name={"placeOfDelivery"}
                  id="placeOfDelivery"
                  value={formik.values.placeOfDelivery || ""}
                  onChange={formik.handleChange}
                  error={!!formik.errors.placeOfDelivery}
                  maxLengthProps={132}
                />
                {getTextError("placeOfDelivery")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Port of Discharge" })}
                </MyTypography>
                <SingleTextField
                  name={"portOfDischarge"}
                  id="portOfDischarge"
                  value={formik.values.portOfDischarge || ""}
                  onChange={formik.handleChange}
                  error={!!formik.errors.portOfDischarge}
                  maxLengthProps={132}
                />
                {getTextError("portOfDischarge")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Port of Loading" })}
                </MyTypography>
                <SingleTextField
                  name={"portOfLoading"}
                  id="portOfLoading"
                  value={formik.values.portOfLoading || ""}
                  onChange={formik.handleChange}
                  error={!!formik.errors.portOfLoading}
                  maxLengthProps={132}
                />
                {getTextError("portOfLoading")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "No. of Containers" })}
                </MyTypography>
                <SingleTextField
                  name={"noOfContainers"}
                  id="noOfContainers"
                  value={formik.values.noOfContainers || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Shipping Mark" })}
                </MyTypography>
                <MyTypography
                  variant={"body3"}
                  className={classes.lineMultiText}
                >
                  {dataExportOrder?.shippingMark}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6} className={classes.selectField}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "UOM" })}
                </MyTypography>
                <SingleTextField
                  name={"uom"}
                  id="uom"
                  value={formik.values.uom || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.selectField}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "G.W.UOM" })}
                </MyTypography>
                <SingleTextField
                  name={"gwUom"}
                  id="gwUom"
                  value={formik.values.gwUom || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "ETD" })}
                </MyTypography>
                <DatePicker
                  name={"etd"}
                  id="etd"
                  value={formik.values.etd}
                  onChange={handleChangeEtd}
                  error={!!formik.errors.etd}
                />
                {getTextError("etd")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "ETA" })}
                </MyTypography>
                <DatePicker
                  name={"eta"}
                  id="eta"
                  value={formik.values.eta}
                  onChange={handleChangeEta}
                  error={!!formik.errors.eta}
                />
                {getTextError("eta")}
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({
                    defaultMessage: "D-L/C : Expiry Date"
                  })}
                </MyTypography>
                <DatePicker
                  name={"dlcExpiryDate"}
                  id="dlcExpiryDate"
                  value={formik.values.dlcExpiryDate}
                  onChange={handleDate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "D-L/C No." })}
                </MyTypography>
                <SingleTextField
                  id="dlcNo"
                  name={"dlcNo"}
                  value={formik.values.dlcNo || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({
                    defaultMessage: "D-L/C : Latest Delivery Date"
                  })}
                </MyTypography>
                <DatePicker
                  name={"dlcLatestDeliveryDate"}
                  id="dlcLatestDeliveryDate"
                  value={formik.values.dlcLatestDeliveryDate}
                  onChange={handleDate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Description" })}
                </MyTypography>
                <SingleTextField
                  name={"description"}
                  id="description"
                  value={formik.values.description || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={132}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "End Customer" })}
                </MyTypography>
                <SingleTextField
                  id="endCustomer"
                  name={"endCustomer"}
                  value={formik.values.endCustomer || ""}
                  onChange={formik.handleChange}
                  maxLengthProps={10}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Contact Person" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.contactPerson}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Sales Employee" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.salesEmployee}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Author" })}
                </MyTypography>
                <MyTypography variant={"body3"}>
                  {dataExportOrder?.author}
                </MyTypography>
              </Grid>
              <Grid item xs={12} md={6} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.pickAddress}>
                <div className={classes.addressContent}>
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.titleField}
                  >
                    {intl.formatMessage({ defaultMessage: "Ship-to Party" })}
                  </MyTypography>
                  <MyTypography
                    style={{
                      whiteSpace: "pre-line"
                    }}
                    variant={"body3"}
                  >
                    {`${formik.values.shipTo}`}
                  </MyTypography>
                </div>
                {isDisplayShipTo ? (
                  <Button
                    className={classes.btnEdit}
                    color={"secondary"}
                    variant={"outlined"}
                    onClick={() =>
                      handleOpenAddress(RealtimePartnerType.SHIP_TO)
                    }
                    name="edit-ship-to-button"
                    id="edit-ship-to-button"
                  >
                    <MyTypography variant={"body3"}>EDIT</MyTypography>
                  </Button>
                ) : null}
              </Grid>
              <Grid item xs={12} className={classes.pickAddress}>
                <div className={classes.addressContent}>
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.titleField}
                  >
                    {intl.formatMessage({ defaultMessage: "Bill-to Party" })}
                  </MyTypography>
                  <MyTypography
                    style={{
                      whiteSpace: "pre-line"
                    }}
                    variant={"body3"}
                  >
                    {formik.values.billTo}
                  </MyTypography>
                </div>
                {isDisplayBillTo ? (
                  <Button
                    className={classes.btnEdit}
                    color={"secondary"}
                    variant={"outlined"}
                    onClick={() =>
                      handleOpenAddress(RealtimePartnerType.BILL_TO)
                    }
                    name="edit-bill-to-button"
                    id="edit-bill-to-button"
                  >
                    <MyTypography variant={"body3"}>EDIT</MyTypography>
                  </Button>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Payer" })}
                </MyTypography>
                <SingleTextField
                  name={"payer"}
                  id="payer"
                  value={formik.values.payer || ""}
                  onChange={formik.handleChange}
                  error={!!formik.errors.payer}
                  maxLengthProps={200}
                />
                {getTextError("payer")}
              </Grid>
              <Grid item xs={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({
                    defaultMessage: "Payment Instruction"
                  })}
                </MyTypography>
                <TextAreaField
                  value={formik.values.paymentInstruction || ""}
                  name={"paymentInstruction"}
                  id="paymentInstruction"
                  onChange={formik.handleChange}
                  title={
                    <div className={classes.titleArea}>
                      <img src={iconComment} alt="icon-comment" />
                      <div>
                        {intl.formatMessage({ defaultMessage: "รายละเอียด" })}
                      </div>
                    </div>
                  }
                  maxLength={1320}
                  lineLimit={132}
                  rowsMax={10}
                />
              </Grid>
              <Grid item xs={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({ defaultMessage: "Remark" })}
                </MyTypography>
                <TextAreaField
                  value={formik.values.remark || ""}
                  name={"remark"}
                  id="remark"
                  onChange={formik.handleChange}
                  title={
                    <div className={classes.titleArea}>
                      <img src={iconComment} alt="icon-comment" />
                      <div>
                        {intl.formatMessage({ defaultMessage: "รายละเอียด" })}
                      </div>
                    </div>
                  }
                  maxLength={1320}
                  lineLimit={132}
                  rowsMax={10}
                />
              </Grid>
              <Grid item xs={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({
                    defaultMessage: "Production information"
                  })}
                </MyTypography>
                <TextAreaField
                  value={formik.values.productionInformation || ""}
                  name={"productionInformation"}
                  id="productionInformation"
                  onChange={formik.handleChange}
                  title={
                    <div className={classes.titleArea}>
                      <img src={iconComment} alt="icon-comment" />
                      <div>
                        {intl.formatMessage({ defaultMessage: "รายละเอียด" })}
                      </div>
                    </div>
                  }
                  maxLength={1320}
                  lineLimit={132}
                  rowsMax={10}
                />
              </Grid>
              <Grid item xs={12}>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.titleField}
                >
                  {intl.formatMessage({
                    defaultMessage: "Internal Comments to Warehouse"
                  })}
                </MyTypography>
                <TextAreaField
                  value={formik.values.internalCommentToWarehouse || ""}
                  name={"internalCommentToWarehouse"}
                  id="internalCommentToWarehouse"
                  onChange={formik.handleChange}
                  title={
                    <div className={classes.titleArea}>
                      <img src={iconComment} alt="icon-comment" />
                      <div>
                        {intl.formatMessage({ defaultMessage: "รายละเอียด" })}
                      </div>
                    </div>
                  }
                  maxLength={1320}
                  lineLimit={132}
                  rowsMax={10}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {visibleModalAddress && (
          <ModalAddress
            address={
              typeAddress === RealtimePartnerType.SHIP_TO
                ? shipToData || []
                : billToData || []
            }
            handleApplyAddress={handleApplyAddress}
            type={typeAddress}
            visible={visibleModalAddress}
            onClose={() => setVisibleModalAddress(false)}
            formik={formik}
            initialValue={
              typeAddress === RealtimePartnerType.SHIP_TO
                ? formik.values.shipTo
                : formik.values.billTo
            }
          />
        )}
        <DialogError
          onClose={() => setVisibleDialogError(false)}
          open={visibleDialogError}
        />
      </div>
    </div>
  );
};

export default OrderHeaderTab;
